import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Medication } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { saveMedicationTemplate } from "@/lib/stores/medicationTemplates";
import { useAuthStore } from "@/lib/auth";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";
import { MedicationForm } from "./MedicationForm";

interface SaveTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  medications: Medication[];
  onTemplateSaved?: () => void;
  allowEdit?: boolean; // When true, show full medication form for inline editing
  initialDiagnosis?: string;
  initialTemplateName?: string;
}

export function SaveTemplateDialog({
  isOpen,
  onClose,
  medications: initialMedications,
  onTemplateSaved,
  allowEdit = false,
  initialDiagnosis = "",
  initialTemplateName = ""
}: SaveTemplateDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [diagnosis, setDiagnosis] = useState(initialDiagnosis);
  const [templateName, setTemplateName] = useState(initialTemplateName);
  const [isSaving, setIsSaving] = useState(false);
  const [medications, setMedications] = useState<Medication[]>(initialMedications);
  
  // Determine if we should show edit mode
  const showEditMode = allowEdit || medications.length === 0;

  const handleSave = async () => {
    if (!diagnosis.trim()) {
      toast({
        title: "Diagnosis required",
        description: "Please enter a diagnosis for the template",
        variant: "destructive"
      });
      return;
    }

    if (!templateName.trim()) {
      toast({
        title: "Template name required",
        description: "Please enter a name for the template",
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication error",
        description: "User ID not found",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      // Get or create doctor profile for the user
      let doctorId = await getDoctorIdForUser(user);
      
      if (!doctorId) {
        console.log('No doctor profile found, creating one...');
        doctorId = await ensureDoctorProfile(user);
        
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }

      console.log('Using doctor ID:', doctorId);

      await saveMedicationTemplate({
        doctorId: doctorId,
        diagnosis: diagnosis.trim(),
        templateName: templateName.trim(),
        medications: medications
      });

      toast({
        title: "Template saved",
        description: `Template "${templateName}" saved successfully for ${diagnosis}`
      });

      // Reset form
      setDiagnosis("");
      setTemplateName("");
      onTemplateSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error saving template",
        description: "Failed to save template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddMedication = (medication: Medication) => {
    setMedications([...medications, medication]);
  };

  const handleAddMultipleMedications = (newMedications: Medication[]) => {
    setMedications([...medications, ...newMedications]);
  };

  const handleRemoveMedication = (index: number) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  const handleUpdateMedication = (medication: Medication, index: number) => {
    const updated = [...medications];
    updated[index] = medication;
    setMedications(updated);
  };

  // Sync medications when dialog opens or initialMedications changes
  useEffect(() => {
    if (!isOpen) return;
    // Prefill fields
    setDiagnosis(initialDiagnosis || "");
    setTemplateName(initialTemplateName || "");
    // Seed medications when provided
    setMedications(initialMedications || []);
  }, [isOpen, initialDiagnosis, initialTemplateName, initialMedications]);

  const handleClose = () => {
    setDiagnosis("");
    setTemplateName("");
    setMedications(initialMedications);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className={showEditMode ? "max-w-6xl max-h-[90vh] overflow-y-auto" : "max-w-4xl max-h-[80vh] overflow-y-auto"}>
        <DialogHeader>
          <DialogTitle>{showEditMode ? "Create New Template" : "Save as Template"}</DialogTitle>
          <DialogDescription>
            {showEditMode 
              ? "Create a new medication template. Add diagnosis, template name, and medications below."
              : "Save your current medications as a reusable template for future prescriptions."
            }
          </DialogDescription>
        </DialogHeader>
        
        {showEditMode ? (
          // Edit mode: Full medication form layout
          <div className="space-y-6">
            {/* Diagnosis and Template Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard ASOM Treatment"
                  className="w-full"
                />
              </div>
            </div>

            {/* Medication Form */}
            <div className="border-t pt-4">
              <MedicationForm
                medications={medications}
                onAddMedication={handleAddMedication}
                onAddMultipleMedications={handleAddMultipleMedications}
                onRemoveMedication={handleRemoveMedication}
                onUpdateMedication={handleUpdateMedication}
              />
            </div>
          </div>
        ) : (
          // Preview mode: Original layout
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left side - Diagnosis and Template Name */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Standard ASOM Treatment"
                  className="w-full"
                />
              </div>

              <div className="text-sm text-muted-foreground">
                <p>This template will be saved to your personal template library and can be used for future patients with similar diagnoses.</p>
              </div>
            </div>

            {/* Right side - Medications Preview */}
            <div className="space-y-4">
              <Label>Medications to Save ({medications.length})</Label>
              <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
                {medications.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No medications to save</p>
                ) : (
                  <div className="space-y-3">
                    {medications.map((medication, index) => (
                      <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                        <div className="font-medium text-sm">
                          {index + 1}. {medication.name}
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>Dosage: {medication.dosage} {medication.doseType}</div>
                          <div>Frequency: {medication.frequency}</div>
                          {medication.frequencyPattern && (
                            <div>Pattern: {medication.frequencyPattern}</div>
                          )}
                          <div>Duration: {medication.duration} days</div>
                          {medication.instructions && (
                            <div>Instructions: {medication.instructions}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || medications.length === 0}
            className="min-w-[120px]"
          >
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
